import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import NavButtons from './NavButtons';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

const LOCATION_TYPES = [
  { id: 'residential', label: 'House',     icon: 'Home',       extraField: null },
  { id: 'apartment',   label: 'Apartment', icon: 'Layers',     extraField: { key: 'delivery_unit',        label: 'Apt / Unit #',    placeholder: 'e.g. Apt 4B',        required: true  } },
  { id: 'business',    label: 'Business',  icon: 'Briefcase',  extraField: { key: 'delivery_business',    label: 'Business Name',   placeholder: 'e.g. Acme Corp',     required: true  } },
  { id: 'office',      label: 'Office',    icon: 'Target',     extraField: { key: 'delivery_business',    label: 'Company / Suite', placeholder: 'e.g. Suite 200',     required: true  } },
  { id: 'hospital',    label: 'Hospital',  icon: 'PlusSquare', extraField: { key: 'delivery_room',        label: 'Room / Ward #',   placeholder: 'e.g. Room 312',      required: false } },
  { id: 'hotel',       label: 'Hotel',     icon: 'MapPin',     extraField: { key: 'delivery_room',        label: 'Room #',          placeholder: 'e.g. Room 214',      required: false } },
];

// Format phone as (XXX) XXX-XXXX
function formatPhone(value) {
  const digits = value.replace(/\D/g, '').substring(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// Validate Arizona ZIP (85000–86999)
function isValidAZZip(zip) {
  if (!/^\d{5}$/.test(zip)) return false;
  const num = parseInt(zip);
  return num >= 85000 && num <= 86999;
}

// Street address must contain at least one number and one letter
function isValidStreetAddress(addr) {
  return /\d/.test(addr) && /[a-zA-Z]/.test(addr) && addr.trim().length >= 5;
}

// Detect iOS — Safari ignores min attribute on date inputs
function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function getMinDate() {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 5);
}

function formatDateValue(dateObj) {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Format as "Wednesday, April 8, 2026"
function formatHumanDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

// ─── iOS Custom Date Picker ──────────────────────────────────────────────────
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function MobileDatePicker({ value, onChange, minDate }) {
  const minDateObj = new Date(minDate + 'T00:00:00');
  const maxDateObj = new Date(minDateObj.getFullYear() + 1, minDateObj.getMonth(), minDateObj.getDate());
  const current = value ? new Date(value + 'T00:00:00') : minDateObj;
  const selYear = current.getFullYear();
  const selMonth = current.getMonth();
  const selDay = current.getDate();

  const years = [];
  for (let y = minDateObj.getFullYear(); y <= maxDateObj.getFullYear(); y++) years.push(y);

  const months = [];
  for (let m = 0; m < 12; m++) {
    const first = new Date(selYear, m, 1);
    const last = new Date(selYear, m + 1, 0);
    if (last >= minDateObj && first <= maxDateObj) months.push(m);
  }

  const days = [];
  const daysInMonth = new Date(selYear, selMonth + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    const candidate = new Date(selYear, selMonth, d);
    if (candidate >= minDateObj && candidate <= maxDateObj) days.push(d);
  }

  const handleChange = (newYear, newMonth, newDay) => {
    const maxDay = new Date(newYear, newMonth + 1, 0).getDate();
    const clampedDay = Math.min(newDay, maxDay);
    const candidate = new Date(newYear, newMonth, clampedDay);
    const final = candidate >= minDateObj ? candidate : minDateObj;
    onChange(formatDateValue(final));
  };

  const selectClass = "flex-1 input-field !py-3 !px-3 text-sm appearance-none bg-white cursor-pointer";

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <select className={selectClass} value={selMonth} onChange={(e) => handleChange(selYear, parseInt(e.target.value), selDay)}>
          {months.map(m => <option key={m} value={m}>{MONTH_NAMES[m]}</option>)}
        </select>
        <select className="w-20 input-field !py-3 !px-3 text-sm appearance-none bg-white cursor-pointer" value={selDay} onChange={(e) => handleChange(selYear, selMonth, parseInt(e.target.value))}>
          {days.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className="w-24 input-field !py-3 !px-3 text-sm appearance-none bg-white cursor-pointer" value={selYear} onChange={(e) => handleChange(parseInt(e.target.value), selMonth, selDay)}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {value && (
        <div className="bg-blush-50 border border-blush-100 rounded-2xl px-4 py-3 text-center">
          <p className="text-blush-500 font-semibold text-sm">{formatHumanDate(value)}</p>
        </div>
      )}
    </div>
  );
}

// Build a list of upcoming valid meal prep delivery dates (cutoff not yet passed).
function getUpcomingMealPrepDates(rules, count = 4) {
  const {
    meal_prep_delivery_days = [1],
    meal_prep_cutoff_day    = 5,
    meal_prep_cutoff_time   = '23:59',
  } = rules;

  const [cutoffHour, cutoffMin] = meal_prep_cutoff_time.split(':').map(Number);
  const now     = new Date();
  const results = [];

  for (let i = 1; results.length < count && i <= 60; i++) {
    const candidate = new Date(now);
    candidate.setDate(now.getDate() + i);
    candidate.setHours(0, 0, 0, 0);

    const dow = candidate.getDay();
    if (!meal_prep_delivery_days.includes(dow)) continue;

    const daysBack   = (dow - meal_prep_cutoff_day + 7) % 7;
    const cutoffDate = new Date(candidate);
    cutoffDate.setDate(candidate.getDate() - daysBack);
    cutoffDate.setHours(cutoffHour, cutoffMin, 0, 0);

    if (now <= cutoffDate) {
      results.push({
        dateStr      : formatDateValue(candidate),
        label        : candidate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
        cutoffLabel  : cutoffDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) +
                       ' by ' + cutoffDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      });
    }
  }
  return results;
}

export default function Step2Delivery({ formData, updateFormData, onNext, onBack, orderRules = {} }) {
  const addressInputRef = useRef(null);
  const [addressError, setAddressError] = useState('');
  const [zipError, setZipError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [onMobile] = useState(() => isIOS());
  const [dateCapError, setDateCapError] = useState('');
  const [checkingCap, setCheckingCap] = useState(false);

  const isNextDay  = formData.delivery_type === 'next_day';
  const isMealPrep = formData.delivery_type === 'meal_prep';

  // Pre-compute the upcoming valid meal prep dates once per render
  const mealPrepDates = isMealPrep ? getUpcomingMealPrepDates(orderRules) : [];

  const minDateObj = isNextDay
    ? (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d; })()
    : getMinDate();
  const minDate = formatDateValue(minDateObj);

  // For next-day, date is already set in Step0 — don't override it
  useEffect(() => {
    if (!isNextDay && !formData.delivery_date) {
      updateFormData({ delivery_date: minDate });
    }
  }, []);

  const checkDateCapacity = async (dateVal) => {
    if (!dateVal || !isSupabaseConfigured()) return;
    setCheckingCap(true);
    setDateCapError('');
    try {
      const { data: settings } = await supabase
        .from('delivery_settings')
        .select('scheduled_daily_limit')
        .single();
      const limit = settings?.scheduled_daily_limit ?? 10;

      const { count } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('delivery_date', dateVal)
        .eq('delivery_type', 'scheduled')
        .not('status', 'eq', 'cancelled');

      const booked = count || 0;
      if (booked >= limit) {
        setDateCapError('This date is fully booked. Please choose another date.');
      }
    } catch (err) {
      console.error('Cap check failed:', err);
    } finally {
      setCheckingCap(false);
    }
  };


  const handleAddressBlur = () => {
    if (formData.delivery_address && !isValidStreetAddress(formData.delivery_address)) {
      setAddressError('Please enter a valid street address including a street number.');
    } else {
      setAddressError('');
    }
  };

  const handleZipChange = (value) => {
    const digits = value.replace(/\D/g, '').substring(0, 5);
    updateFormData({ delivery_zip: digits });
    if (digits.length === 5 && !isValidAZZip(digits)) {
      setZipError('Please enter a valid Arizona ZIP code (85000–86999).');
    } else {
      setZipError('');
    }
  };

  const handleRecipientPhone = (value) => {
    const formatted = formatPhone(value);
    updateFormData({ recipient_phone: formatted });
    const digits = value.replace(/\D/g, '');
    if (digits.length > 0 && digits.length < 10) {
      setPhoneError('Please enter a complete 10-digit phone number.');
    } else {
      setPhoneError('');
    }
  };

  const isDateValid = isMealPrep
    ? (!!formData.delivery_date && mealPrepDates.some(d => d.dateStr === formData.delivery_date))
    : (formData.delivery_date && formData.delivery_date >= minDate);
  const recipientPhoneDigits = (formData.recipient_phone || '').replace(/\D/g, '');
  const isPhoneValid = recipientPhoneDigits.length === 10;
  const isZipValid = !formData.delivery_zip || isValidAZZip(formData.delivery_zip);
  const isAddressValid = !formData.delivery_address || isValidStreetAddress(formData.delivery_address);

  const selectedLocationType = LOCATION_TYPES.find(t => t.id === formData.delivery_location_type);
  const extraField = selectedLocationType?.extraField || null;
  const extraFieldValid = !extraField?.required || !!formData[extraField.key]?.trim();

  const isValid =
    isDateValid &&
    (isMealPrep || (!dateCapError && !checkingCap)) &&
    formData.delivery_time &&
    formData.recipient_name &&
    formData.delivery_address &&
    isAddressValid &&
    formData.delivery_city &&
    formData.delivery_zip &&
    formData.delivery_zip.length === 5 &&
    isZipValid &&
    formData.delivery_location_type &&
    extraFieldValid &&
    isPhoneValid &&
    recipientPhoneDigits.length === 10 &&
    formData.address_confirmed === true;

  const cardClass = "bg-white rounded-[2.5rem] p-6 md:p-10 border border-sage-100 shadow-soft space-y-8";

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-10">
      <div className="text-center space-y-3">
        <h2 className="text-4xl md:text-5xl font-serif text-charcoal">Delivery Details</h2>
        <p className="text-sage-500 text-lg md:text-xl font-medium">
          {isMealPrep
            ? 'Pick your delivery week and drop-off address.'
            : 'When and where should your order arrive?'}
        </p>
      </div>

      <div className="space-y-8">
        {/* Date Selection */}
        <div className="bg-white rounded-[2.5rem] p-6 md:p-8 border border-sage-100 shadow-sm space-y-6">
          <div className="field-group w-full max-w-sm mx-auto md:max-w-md">
            <label className="form-label text-center mb-3">
              {isMealPrep ? 'Select Delivery Week *' : 'Delivery Date *'}
            </label>

            {/* ── Meal Prep: show upcoming valid Mondays ── */}
            {isMealPrep ? (
              mealPrepDates.length === 0 ? (
                <div className="text-center py-6 space-y-2">
                  <SafeIcon name="Calendar" className="text-4xl text-sage-300 mx-auto" />
                  <p className="font-bold text-charcoal">No available dates right now.</p>
                  <p className="text-sm text-sage-400">
                    Ordering may be closed for this week. Check back soon or{' '}
                    <a href="mailto:hi@phoenixkabob.com" className="text-blush-400 font-semibold underline">contact us</a>.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {mealPrepDates.map(d => {
                    const isSelected = formData.delivery_date === d.dateStr;
                    return (
                      <button
                        key={d.dateStr}
                        type="button"
                        onClick={() => updateFormData({ delivery_date: d.dateStr })}
                        className={`w-full text-left rounded-2xl p-4 border-2 transition-all ${
                          isSelected
                            ? 'border-blush-300 bg-blush-50/30 ring-2 ring-blush-100'
                            : 'border-sage-100 bg-sage-50/30 hover:border-blush-200'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className={`font-bold text-sm ${isSelected ? 'text-blush-500' : 'text-charcoal'}`}>
                              {d.label}
                            </p>
                            <p className="text-xs text-sage-400 mt-0.5">Order by {d.cutoffLabel}</p>
                          </div>
                          {isSelected && (
                            <div className="w-6 h-6 bg-blush-300 rounded-full flex items-center justify-center shrink-0">
                              <SafeIcon name="Check" className="text-white text-xs" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )
            ) : isNextDay ? (
              <div className="space-y-3">
                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl px-5 py-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-amber-500 text-lg">⚡</span>
                    <p className="text-amber-700 font-bold text-base">Next Day Delivery</p>
                  </div>
                  <p className="text-amber-600 font-semibold text-sm">{formatHumanDate(formData.delivery_date)}</p>
                </div>
                <p className="text-center text-sage-400 italic text-[10px] uppercase tracking-widest font-bold">
                  Order now — delivered tomorrow
                </p>
              </div>
            ) : onMobile ? (
              <MobileDatePicker
                value={formData.delivery_date || minDate}
                onChange={(val) => {
                  updateFormData({ delivery_date: val });
                  checkDateCapacity(val);
                }}
                minDate={minDate}
              />
            ) : (
              <>
                <input
                  type="date"
                  min={minDate}
                  className={`input-field w-full ${(formData.delivery_date && !isDateValid) || dateCapError ? 'border-red-300 bg-red-50' : ''}`}
                  value={formData.delivery_date || ''}
                  onChange={(e) => {
                    updateFormData({ delivery_date: e.target.value });
                    checkDateCapacity(e.target.value);
                  }}
                />
                {formData.delivery_date && isDateValid && !dateCapError && (
                  <div className="bg-blush-50 border border-blush-100 rounded-2xl px-4 py-3 text-center mt-3">
                    <p className="text-blush-500 font-semibold text-sm">{formatHumanDate(formData.delivery_date)}</p>
                  </div>
                )}
              </>
            )}

            {!isNextDay && !isMealPrep && (
              <p className="text-center text-sage-400 italic text-[10px] uppercase tracking-widest font-bold mt-3">
                We require 5 days to prepare your custom delivery
              </p>
            )}
            {formData.delivery_date && !isDateValid && !isMealPrep && (
              <p className="text-red-500 text-[10px] font-bold text-center mt-1 uppercase tracking-tighter">
                Please select a date at least 5 days from today
              </p>
            )}
            {!isMealPrep && checkingCap && (
              <p className="text-sage-400 text-[10px] font-bold text-center mt-1 uppercase tracking-wider flex items-center justify-center gap-1">
                <span className="inline-block w-2.5 h-2.5 border-2 border-sage-300 border-t-transparent rounded-full animate-spin" />
                Checking availability…
              </p>
            )}
            {!isMealPrep && dateCapError && (
              <p className="text-red-500 text-[10px] font-bold text-center mt-1 uppercase tracking-tighter">
                {dateCapError}
              </p>
            )}
          </div>

          {/* Delivery Time Selection */}
          <div className="field-group w-full max-w-sm mx-auto md:max-w-md border-t border-sage-100 pt-6">
            <label className="form-label text-center mb-3">Delivery Time *</label>
            <div>
              <select
                className="input-field w-full"
                value={formData.delivery_time || ''}
                onChange={(e) => updateFormData({ delivery_time: e.target.value })}
              >
                <option value="">Select a delivery time</option>
                <option value="09:00">9:00 AM (8:30–9:30 AM)</option>
                <option value="10:00">10:00 AM (9:30–10:30 AM)</option>
                <option value="11:00">11:00 AM (10:30–11:30 AM)</option>
                <option value="12:00">12:00 PM (11:30 AM–12:30 PM)</option>
                <option value="13:00">1:00 PM (12:30–1:30 PM)</option>
                <option value="14:00">2:00 PM (1:30–2:30 PM)</option>
                <option value="15:00">3:00 PM (2:30–3:30 PM)</option>
                <option value="16:00">4:00 PM (3:30–4:30 PM)</option>
                <option value="17:00">5:00 PM (4:30–5:30 PM)</option>
              </select>
              {formData.delivery_time && (
                <div className="bg-blush-50 border border-blush-100 rounded-2xl px-4 py-3 text-center mt-3">
                  <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest mb-1">Delivery Window</p>
                  <p className="text-blush-500 font-semibold text-sm">30 minutes before or after your selected time</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recipient Details */}
        <div className={cardClass}>
          <div className="flex items-center gap-3 border-b border-sage-50 pb-4">
            <div className="w-10 h-10 bg-sage-50 rounded-full flex items-center justify-center text-sage-400 shrink-0">
              <SafeIcon name="User" />
            </div>
            <h3 className="font-serif text-xl md:text-2xl text-charcoal">Recipient Details</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="field-group">
              <label className="form-label">Recipient's Name *</label>
              <input
                type="text"
                placeholder="e.g. John Smith"
                className="input-field"
                value={formData.recipient_name || ''}
                onChange={(e) => updateFormData({ recipient_name: e.target.value })}
              />
            </div>
            <div className="field-group">
              <label className="form-label">Recipient's Phone *</label>
              <input
                type="tel"
                placeholder="(555) 000-0000"
                className={`input-field ${phoneError ? 'border-red-300 bg-red-50' : ''}`}
                value={formData.recipient_phone || ''}
                onChange={(e) => handleRecipientPhone(e.target.value)}
                onBlur={() => {
                  const digits = (formData.recipient_phone || '').replace(/\D/g, '');
                  if (digits.length > 0 && digits.length < 10) {
                    setPhoneError('Please enter a complete 10-digit phone number.');
                  }
                }}
              />
              {phoneError && (
                <p className="text-red-500 text-[10px] font-bold uppercase tracking-wider mt-1">{phoneError}</p>
              )}
              <p className="text-[10px] text-sage-400 mt-2">For driver coordination on delivery day</p>
            </div>
          </div>
        </div>

        {/* Address Card */}
        <div className={cardClass}>
          <div className="flex items-center gap-3 border-b border-sage-50 pb-4">
            <div className="w-10 h-10 bg-sage-50 rounded-full flex items-center justify-center text-sage-400 shrink-0">
              <SafeIcon name="MapPin" />
            </div>
            <h3 className="font-serif text-xl md:text-2xl text-charcoal">Delivery Address</h3>
          </div>
          <div className="space-y-8">

            {/* Delivery Zone Notice */}
            <div className="bg-blush-50 border-2 border-blush-200 rounded-2xl p-4 flex gap-3 items-start">
              <div className="w-8 h-8 bg-blush-300 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <SafeIcon name="MapPin" className="text-white text-sm" />
              </div>
              <div>
                <p className="text-sm font-bold text-charcoal">Currently delivering to select West Valley cities</p>
                <p className="text-sm text-sage-500 mt-1">
                  We are currently hand delivering in{' '}
                  <span className="font-semibold text-charcoal">Surprise</span>,{' '}
                  <span className="font-semibold text-charcoal">Sun City</span>, and{' '}
                  <span className="font-semibold text-charcoal">El Mirage</span>.
                  If your delivery city is not listed, please{' '}
                  <a href="mailto:hi@phoenixkabob.com" className="text-blush-400 font-semibold underline underline-offset-2">contact us</a>{' '}
                  before placing an order.
                </p>
              </div>
            </div>

            {/* Location Type Picker */}
            <div className="field-group">
              <label className="form-label mb-4">Location Type *</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {LOCATION_TYPES.map((type) => {
                  const isSelected = formData.delivery_location_type === type.id;
                  return (
                    <button
                      key={type.id}
                      onClick={() => updateFormData({ delivery_location_type: type.id })}
                      className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all text-sm font-semibold ${
                        isSelected ? 'bg-blush-300 border-blush-300 text-white shadow-md' : 'bg-white border-sage-100 text-sage-500 hover:border-blush-100'
                      }`}
                    >
                      <SafeIcon name={type.icon} className={isSelected ? 'text-white' : 'text-blush-300'} />
                      {type.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Street Address with Google Autocomplete */}
            <div className="field-group">
              <label className="form-label">Street Address *</label>
              <div className="relative">
                <input
                  ref={addressInputRef}
                  type="text"
                  placeholder="123 Floral Lane"
                  className={`input-field ${addressError ? 'border-red-300 bg-red-50' : ''}`}
                  value={formData.delivery_address || ''}
                  onChange={(e) => {
                    updateFormData({ delivery_address: e.target.value });
                    if (addressError) setAddressError('');
                  }}
                  onBlur={handleAddressBlur}
                  autoComplete="off"
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <SafeIcon name="MapPin" className="text-sage-300 text-lg" />
                </div>
              </div>
              {addressError && (
                <p className="text-red-500 text-[10px] font-bold uppercase tracking-wider mt-1">{addressError}</p>
              )}
            </div>

            {/* Conditional extra field based on location type */}
            {extraField && (
              <motion.div
                key={extraField.key + extraField.label}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="field-group"
              >
                <label className="form-label">
                  {extraField.label} {extraField.required ? '*' : <span className="text-sage-400 font-normal normal-case tracking-normal">(optional)</span>}
                </label>
                <input
                  type="text"
                  placeholder={extraField.placeholder}
                  className="input-field"
                  value={formData[extraField.key] || ''}
                  onChange={(e) => updateFormData({ [extraField.key]: e.target.value })}
                />
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="field-group">
                <label className="form-label">City *</label>
                <input
                  type="text"
                  placeholder="Phoenix"
                  className="input-field"
                  value={formData.delivery_city || ''}
                  onChange={(e) => updateFormData({ delivery_city: e.target.value })}
                />
              </div>
              <div className="field-group">
                <label className="form-label">ZIP Code *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="85001"
                  maxLength={5}
                  className={`input-field ${zipError ? 'border-red-300 bg-red-50' : ''}`}
                  value={formData.delivery_zip || ''}
                  onChange={(e) => handleZipChange(e.target.value)}
                />
                {zipError && (
                  <p className="text-red-500 text-[10px] font-bold uppercase tracking-wider mt-1">{zipError}</p>
                )}
              </div>
            </div>

            <div className="bg-sage-50/50 border border-sage-100 rounded-2xl p-5 flex items-start gap-4 mb-6">
              <div className="mt-1">
                <div className="relative">
                  <div className="absolute inset-0 bg-blush-300 rounded-full animate-ping opacity-25"></div>
                  <SafeIcon name="AlertCircle" className="relative text-blush-400 text-xl" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-charcoal font-bold text-base leading-snug">
                  Please double-check the street number, apartment, and ZIP code are correct before continuing.
                </p>
              </div>
            </div>

            {/* Address Confirmation Checkbox */}
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => updateFormData({ address_confirmed: !formData.address_confirmed })}
                className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all mt-0.5 ${
                  formData.address_confirmed
                    ? 'bg-blush-300 border-blush-300'
                    : 'bg-white border-sage-200 hover:border-blush-300'
                }`}
              >
                {formData.address_confirmed && (
                  <SafeIcon name="Check" className="text-white text-sm" />
                )}
              </button>
              <label className="text-sm text-charcoal font-medium cursor-pointer">
                I confirm this address is correct
              </label>
            </div>
          </div>
        </div>

        {/* Delivery Notes */}
        <div className="bg-white rounded-[2.5rem] p-6 md:p-12 border border-sage-100 shadow-soft">
          <div className="field-group w-full max-w-none">
            <div className="flex items-center justify-between mb-4">
              <label className="form-label !text-charcoal !text-sm !font-serif !normal-case !tracking-normal">Delivery Notes</label>
              <span className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Optional</span>
            </div>
            <textarea
              rows="4"
              placeholder="Gate code, leave with concierge, side door, etc."
              className="input-field resize-none leading-relaxed h-auto"
              value={formData.special_instructions || ''}
              onChange={(e) => updateFormData({ special_instructions: e.target.value })}
            />
          </div>
        </div>
      </div>
      <NavButtons onNext={onNext} onBack={onBack} disabled={!isValid} nextLabel="Your Information" />
    </motion.div>
  );
}
